import React, {
  createContext,
  useState,
  useContext,
  useMemo,
  useCallback,
  useRef
} from 'react';
import { useSelector } from 'react-redux';

const AdminContext = createContext();

export const AdminProvider = ({ children }) => {
  const user = useSelector((state) => state.auth.user);
  const isAdmin = ['backoffice', 'superuser'].includes(user?.type);
  const isSuperAdmin = user?.type === 'superuser';

  const [isAdminView, setIsAdminView] = useState(false);
  const undoStackRef = useRef([]);
  const [undoCount, setUndoCount] = useState(0);

  // Active in-place editors (the open CKEditor / rich-text field) register an
  // undo handler here while they're being edited. The Undo button calls the
  // most-recently-registered handler first so it behaves like Ctrl+Z — one
  // step back inside the live editor — and only falls through to reverting a
  // saved change once the editor has nothing left to undo (CON-183).
  // activeUndoAvailable tracks whether the open editor actually HAS an undo
  // step right now (reported by the editor), so the button reflects real
  // availability rather than merely "an editor is open".
  const activeUndoHandlersRef = useRef([]);
  const [activeUndoAvailable, setActiveUndoAvailable] = useState(false);

  // Autosave status, surfaced by the navbar SaveStatusIndicator (CON-190).
  // Counter-based so several editors saving at once aggregate to one "Saving…"
  // and only settle to "saved" when the last in-flight save resolves.
  // lastSavedAt (+ the backend's updated_by) is also the future hook for a
  // "last edited by X at Y" / version-history surface.
  const [savingCount, setSavingCount] = useState(0);
  const [saveError, setSaveError] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);

  // Super-admin / agent state. agentSession is opaque on the client today; the
  // backend will own real session IDs once Phase 1 wires the Claude runtime.
  // Until then this keeps a stable handle the ChatDock can rely on for
  // optimistic UI like "recent actions" without re-fetching on every open.
  const [superAdminMode, setSuperAdminMode] = useState(false);
  const [agentDockOpen, setAgentDockOpen] = useState(false);
  const [agentSession, setAgentSession] = useState(null);
  // Pending agent request — set by UI surfaces (e.g. user table row buttons)
  // and consumed by ChatDock on next render. ChatDock acks by calling
  // consumeAgentRequest() so the same request isn't re-fired on re-render.
  const [agentRequest, setAgentRequest] = useState(null);

  const toggleAdminView = useCallback(() => {
    setIsAdminView((prev) => !prev);
  }, []);

  // An editor registers its undo handler while active and unregisters on
  // teardown. handler() should perform one undo step and return true, or
  // return false when there's nothing left to undo inside that editor.
  const registerActiveUndo = useCallback((handler) => {
    activeUndoHandlersRef.current.push(handler);
    return () => {
      activeUndoHandlersRef.current = activeUndoHandlersRef.current.filter(
        (h) => h !== handler
      );
      // No editor left → nothing is undoable via the active path.
      if (activeUndoHandlersRef.current.length === 0) {
        setActiveUndoAvailable(false);
      }
    };
  }, []);

  // The active editor reports whether it currently has an undo step, so the
  // Undo button enables/colours only when there's actually something to undo.
  const reportActiveUndo = useCallback((available) => {
    setActiveUndoAvailable(!!available);
  }, []);

  // Autosave reporting (CON-190). An editor calls reportSaveStart when an edit
  // begins and reportSaveSuccess/Error/Cancel when it settles. Functional
  // updates keep the in-flight counter correct even with concurrent saves.
  const reportSaveStart = useCallback(() => {
    setSaveError(false);
    setSavingCount((n) => n + 1);
  }, []);

  const reportSaveSuccess = useCallback(() => {
    setSavingCount((n) => Math.max(0, n - 1));
    setLastSavedAt(Date.now());
  }, []);

  const reportSaveError = useCallback(() => {
    setSavingCount((n) => Math.max(0, n - 1));
    setSaveError(true);
  }, []);

  // Clears an optimistic "Saving…" that ends WITHOUT a save actually happening
  // (e.g. edits reverted to the saved value before the debounce fired). Drops
  // the in-flight count only — it must NOT stamp lastSavedAt, so "Saved" never
  // shows unless a real save was made (CON-190 follow-up).
  const reportSaveCancel = useCallback(() => {
    setSavingCount((n) => Math.max(0, n - 1));
  }, []);

  // Ask the most-recently-registered editor to undo one step. Returns true if
  // it handled the undo; false means no active editor (or nothing left to
  // undo there) so the caller should fall back to the saved-changes stack.
  //
  // Selection is LIFO (registration order), not focus — a pragmatic choice
  // since admins edit one field at a time. If the top editor has nothing left
  // to undo it returns false and we walk to the next, so the common flow self
  // corrects; only several simultaneously-dirty editors could undo a field
  // other than the focused one.
  const runActiveUndo = useCallback(() => {
    const handlers = activeUndoHandlersRef.current;
    for (let i = handlers.length - 1; i >= 0; i -= 1) {
      try {
        if (handlers[i]() === true) return true;
      } catch {
        // A dead/detached editor handler shouldn't block the saved-change
        // fallback — ignore it and keep looking.
      }
    }
    return false;
  }, []);

  const pushUndo = useCallback((entry) => {
    undoStackRef.current.push(entry);
    setUndoCount((prev) => prev + 1);
  }, []);

  const popUndo = useCallback(() => {
    if (undoStackRef.current.length === 0) return null;
    const entry = undoStackRef.current.pop();
    setUndoCount((prev) => prev - 1);
    return entry;
  }, []);

  const openAgentDock = useCallback(() => {
    if (!isSuperAdmin) return;
    setAgentDockOpen(true);
    setAgentSession((prev) => prev || { id: `local-${Date.now()}`, startedAt: Date.now() });
  }, [isSuperAdmin]);

  const closeAgentDock = useCallback(() => {
    setAgentDockOpen(false);
  }, []);

  const toggleAgentDock = useCallback(() => {
    if (!isSuperAdmin) return;
    setAgentDockOpen((prev) => {
      if (!prev) {
        setAgentSession((cur) => cur || { id: `local-${Date.now()}`, startedAt: Date.now() });
      }
      return !prev;
    });
  }, [isSuperAdmin]);

  const enterSuperAdminMode = useCallback(() => {
    if (!isSuperAdmin) return;
    setSuperAdminMode(true);
  }, [isSuperAdmin]);

  const exitSuperAdminMode = useCallback(() => {
    setSuperAdminMode(false);
  }, []);

  // Public surface: UI surfaces call requestAgentPreview to pop the dock
  // and queue a preview_action call. ChatDock owns the actual invocation
  // so the conversation history stays consistent.
  const requestAgentPreview = useCallback(
    ({ action, params, narration }) => {
      if (!isSuperAdmin) return;
      setAgentDockOpen(true);
      setAgentSession((cur) => cur || { id: `local-${Date.now()}`, startedAt: Date.now() });
      setAgentRequest({
        kind: 'preview',
        id: `req-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        action,
        params,
        narration
      });
    },
    [isSuperAdmin]
  );

  const consumeAgentRequest = useCallback(() => {
    setAgentRequest(null);
  }, []);

  // Aggregate autosave status for the navbar indicator (CON-190).
  let saveStatus = 'idle';
  if (savingCount > 0) saveStatus = 'saving';
  else if (saveError) saveStatus = 'error';
  else if (lastSavedAt) saveStatus = 'saved';

  const contextValue = useMemo(
    () => ({
      isAdmin,
      isAdminView: isAdmin && isAdminView,
      toggleAdminView,
      registerActiveUndo,
      reportActiveUndo,
      runActiveUndo,
      hasActiveUndo: activeUndoAvailable,
      pushUndo,
      popUndo,
      hasUndo: undoCount > 0,
      // Autosave status (CON-190)
      reportSaveStart,
      reportSaveSuccess,
      reportSaveError,
      reportSaveCancel,
      saveStatus,
      lastSavedAt,
      // Super-admin surface
      isSuperAdmin,
      superAdminMode,
      enterSuperAdminMode,
      exitSuperAdminMode,
      agentDockOpen,
      openAgentDock,
      closeAgentDock,
      toggleAgentDock,
      agentSession,
      agentRequest,
      requestAgentPreview,
      consumeAgentRequest
    }),
    [
      isAdmin,
      isAdminView,
      toggleAdminView,
      registerActiveUndo,
      reportActiveUndo,
      runActiveUndo,
      activeUndoAvailable,
      pushUndo,
      popUndo,
      undoCount,
      reportSaveStart,
      reportSaveSuccess,
      reportSaveError,
      reportSaveCancel,
      saveStatus,
      lastSavedAt,
      isSuperAdmin,
      superAdminMode,
      enterSuperAdminMode,
      exitSuperAdminMode,
      agentDockOpen,
      openAgentDock,
      closeAgentDock,
      toggleAgentDock,
      agentSession,
      agentRequest,
      requestAgentPreview,
      consumeAgentRequest
    ]
  );

  return (
    <AdminContext.Provider value={contextValue}>
      {children}
    </AdminContext.Provider>
  );
};

export const useAdmin = () => useContext(AdminContext);
